import React from 'react';
import { alpha, Box, IconButton } from '@mui/material';
import ChevronLeftRounded from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';

export interface ScrollAreaProps {
  children: React.ReactNode;
  /**
   * Width, px, pinned at the start that does not scroll — sticky columns. The
   * start-side shadow and arrow sit after it, not over it.
   */
  insetStart?: number;
  /** Where the arrows sit, px from the top. Defaults to the middle. */
  controlsTop?: number;
  /** Scroll vertically too beyond this height. */
  maxHeight?: number | string;
  /**
   * Take the height a bounded parent gives it and scroll within that, rather
   * than growing — inside a flex column that ends at the bottom of a pane.
   */
  fill?: boolean;
  /** Names the area for screen readers, e.g. "Placement summary table". */
  ariaLabel: string;
  /**
   * Leave out the start-side shadow, for content whose pinned part draws its
   * own edge (a table's last sticky column). The area's shadow runs its full
   * height, so it would also cross any row that spans the pinned part, such
   * as a row opened to show details.
   */
  noStartShade?: boolean;
}

/**
 * A box that scrolls sideways and makes it obvious that it does.
 *
 * Wide content in a narrow box gives no hint that more is hidden, so a soft
 * shadow appears on whichever side has more to see, with a round arrow button
 * there that moves most of a screen's width at a time. Both disappear at the
 * end they have reached. The area is focusable, so arrow keys scroll it too.
 *
 * Sets `data-scrolled-start` on itself once it has left the start, so pinned
 * content inside (a sticky column) can show its own edge shadow, and
 * `data-overflowing` while its content is wider than it is — which lets a
 * table inside wrap its column headings only when the width is actually
 * short, rather than always.
 */
/** Room to spare before wrapped headings are allowed to unwrap again, px. */
const SETTLE = 24;

export function ScrollArea({
  children, insetStart = 0, controlsTop, maxHeight, fill = false, ariaLabel, noStartShade = false,
}: ScrollAreaProps) {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [edges, setEdges] = React.useState({ start: false, end: false, overflowing: false });

  const update = React.useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const content = el.firstElementChild as HTMLElement | null;
    const contentWidth = content ? content.getBoundingClientRect().width : el.scrollWidth;
    setEdges((prev) => {
      // Wrapping a table's headings makes it narrower, which would clear the
      // overflow, which would unwrap the headings and make it wider again.
      // Measured on the content rather than on scrollWidth (which never falls
      // below the visible width, so it cannot tell "just fits" from "fits
      // easily"), and it only lets go once there is room to spare — otherwise
      // hiding a column could leave a table wider than before.
      const overflowing = prev.overflowing
        ? contentWidth > el.clientWidth - SETTLE
        : contentWidth > el.clientWidth + 1;
      const next = { start: el.scrollLeft > 1, end: el.scrollLeft < max - 1, overflowing };
      return prev.start === next.start && prev.end === next.end && prev.overflowing === next.overflowing
        ? prev
        : next;
    });
  }, []);

  React.useLayoutEffect(() => {
    update();
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(el);
    if (el.firstElementChild) observer.observe(el.firstElementChild);
    return () => observer.disconnect();
  }, [update]);

  const scrollBy = (direction: 1 | -1) => {
    const el = ref.current;
    if (!el) return;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    el.scrollBy({ left: direction * (el.clientWidth - insetStart) * 0.8, behavior: reduced ? 'auto' : 'smooth' });
  };

  const arrowTop = controlsTop ?? '50%';

  return (
    <Box
      sx={{
        position: 'relative',
        minWidth: 0,
        ...(fill && { flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' }),
      }}
    >
      <Box
        ref={ref}
        onScroll={update}
        tabIndex={0}
        role="region"
        aria-label={ariaLabel}
        data-scrolled-start={edges.start}
        data-overflowing={edges.overflowing}
        sx={(t) => ({
          overflow: 'auto',
          maxHeight,
          ...(fill && { flex: '1 1 auto', minHeight: 0 }),
          containerType: 'inline-size',
          '&:focus-visible': { outline: `2px solid ${t.tokens.border.focus}`, outlineOffset: -2 },
        })}
      >
        {children}
      </Box>

      {(noStartShade ? ['end'] as const : ['start', 'end'] as const).map((side) => (
        <Box
          key={side}
          aria-hidden
          sx={(t) => ({
            position: 'absolute',
            top: 0,
            bottom: 0,
            width: 24,
            pointerEvents: 'none',
            opacity: edges[side] ? 1 : 0,
            transition: `opacity ${t.ds.motion.duration.normal}ms ${t.ds.motion.easing.standard}`,
            ...(side === 'start'
              ? { left: insetStart, background: `linear-gradient(to right, ${alpha(t.tokens.text.primary, 0.1)}, transparent)` }
              : { right: 0, background: `linear-gradient(to left, ${alpha(t.tokens.text.primary, 0.1)}, transparent)` }),
          })}
        />
      ))}

      {(['start', 'end'] as const).map((side) =>
        edges[side] ? (
          <IconButton
            key={`arrow-${side}`}
            size="small"
            tabIndex={-1}
            aria-label={side === 'start' ? 'Scroll left' : 'Scroll right'}
            onClick={() => scrollBy(side === 'start' ? -1 : 1)}
            sx={(t) => ({
              position: 'absolute',
              top: arrowTop,
              transform: 'translateY(-50%)',
              zIndex: 3,
              width: t.ds.sizes.control.sm,
              height: t.ds.sizes.control.sm,
              bgcolor: t.tokens.surface.overlay,
              border: `1px solid ${t.tokens.border.default}`,
              boxShadow: t.tokens.shadow.sm,
              color: t.tokens.text.secondary,
              '&:hover': { bgcolor: t.tokens.surface.overlay, color: t.tokens.text.primary },
              ...(side === 'start' ? { left: insetStart + 8 } : { right: 8 }),
            })}
          >
            {side === 'start' ? <ChevronLeftRounded fontSize="small" /> : <ChevronRightRounded fontSize="small" />}
          </IconButton>
        ) : null,
      )}
    </Box>
  );
}
